import CampaignAsset from "@/assets/illustrations/camera.webp";
import CardAsset from "@/assets/illustrations/card.webp";
import ClockAsset from "@/assets/illustrations/clock.webp";
import { useDraftReadyMoment } from "@/components/campaign/ai/DraftReadyMoment";
import LegacyChatNotice from "@/components/campaign/ai/LegacyChatNotice";
import CreatorPreview from "@/components/campaign/ai/creatorPreview";
import useAgentChat from "@repo/components/src/hooks/agent/useAgentChat";
import { useIsGuestChat } from "@repo/components/src/hooks/agent/useChatDocuments";
import AgentChat from "@repo/components/src/shared/agent/AgentChat";
import Segmented from "@repo/components/src/shared/segmented";
import { Box } from "@repo/components/src/ui/box";
import { Card } from "@repo/components/src/ui/card";
import { HStack } from "@repo/components/src/ui/hstack";
import { VStack } from "@repo/components/src/ui/vstack";
import { useBreakpoint } from "@repo/components/src/utils/mediaQuery";
import React, { useState } from "react";
import { ScrollView } from "react-native";

/** The glass illustrations `AnimationSlot`'s moments are built around — copied
 * (never edited in place) from the founder's source assets into
 * `assets/illustrations/`. */
const ILLUSTRATIONS = {
  camera: CampaignAsset,
  card: CardAsset,
  clock: ClockAsset,
};

type Panel = "chat" | "preview";

const PANELS = [
  { key: "chat", label: "Chat" },
  { key: "preview", label: "Previsualización" },
] as const;

type Props = {
  brandId: string;
  sessionId: string;
  /** A plan checkout that landed back on this chat is being confirmed. */
  verifyingPayment?: boolean;
};

/**
 * The campaign chat and the campaign taking shape next to it — shared by the
 * logged-in route and the public `/start`.
 *
 * Side by side from `md` up (2/3 chat, 1/3 preview); below it the two are tabs, and the
 * hidden one stays mounted so the chat's websocket subscription and the preview's
 * draft are not torn down by a tab switch.
 *
 * The chat hook lives here, not inside AgentChat, because it owns the one websocket
 * subscription for this chat — and the draft preview needs the same turn state the
 * transcript is rendering. Two calls would mean two sockets.
 */
const CampaignChatView = React.memo(
  ({ brandId, sessionId, verifyingPayment = false }: Props) => {
    const chat = useAgentChat(sessionId);
    // The chat of a start with no account (`/start`, under `GuestChatScopeProvider`):
    // publishing goes to sign-up, and the manual wizard and the retired-chat notice
    // (both behind a login) are left out.
    const guest = useIsGuestChat();
    const { isBelow } = useBreakpoint();
    const stacked = isBelow("md");
    const [panel, setPanel] = useState<Panel>("chat");

    // Moment 4 of the animation system — a hook, not a component, and called
    // unconditionally here for exactly that reason: it has to run on every render.
    const finalMoment = useDraftReadyMoment({
      brandId,
      sessionId,
      draftId: chat.session?.subjectId ?? undefined,
      agentKey: chat.session?.agentKey,
      stacked,
      verifyingPayment,
    });

    const chatCard = (
      <Card
        variant="filled"
        size={stacked ? "sm" : "lg"}
        className="max-h-full h-full border overflow-hidden"
      >
        <AgentChat
          chat={chat}
          placeholder="Pídeme cambios en tu campaña…"
          illustrations={ILLUSTRATIONS}
          finalMoment={finalMoment}
          readOnlyFooter={guest ? null : <LegacyChatNotice brandId={brandId} />}
        />
      </Card>
    );

    const previewCard = (
      <ScrollView showsVerticalScrollIndicator={false}>
        <Card variant="filled" size={stacked ? "sm" : "lg"} className="border">
          <CreatorPreview
            brandId={brandId}
            draftId={chat.session?.subjectId ?? undefined}
            tools={chat.tools}
            turnPhase={chat.turn.phase}
            agentKey={chat.session?.agentKey}
            refreshKey={chat.draftRevision}
          />
        </Card>
      </ScrollView>
    );

    if (stacked) {
      return (
        <VStack space="sm" className="h-full">
          <Segmented
            options={[...PANELS]}
            value={panel}
            onChange={setPanel}
            fill
          />
          <Box className={panel === "chat" ? "flex-1 min-h-0" : "hidden"}>
            {chatCard}
          </Box>
          <Box className={panel === "preview" ? "flex-1 min-h-0" : "hidden"}>
            {previewCard}
          </Box>
        </VStack>
      );
    }

    return (
      <HStack flexSpace="md">
        <Box className="w-2/3 h-full">{chatCard}</Box>
        <Box className="w-1/3 h-full">{previewCard}</Box>
      </HStack>
    );
  },
);

CampaignChatView.displayName = "CampaignChatView";

export default CampaignChatView;
